import type { ForwardedRef, PropsWithChildren } from 'react';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { mergeRefs } from '@react-aria/utils';
import { theme } from '@frameio/vapor-theme';
import { useIsomorphicLayoutEffect } from '../../../hooks/useIsomorphicLayoutEffect';

type RippleProps = PropsWithChildren<{
  width?: number;
  height?: number;
  startScale?: number;
  endScale?: number;
  enabled?: boolean;
  isPressed?: boolean;
  isDisabled?: boolean;
}>;

export const Ripple = React.forwardRef(
  (
    {
      enabled = false,
      width = 24,
      height = 24,
      startScale = 0.3,
      endScale = 2,
      children,
      isDisabled = false,
    }: RippleProps,
    forwardedRef: ForwardedRef<HTMLSpanElement>
  ) => {
    const rippleRef = useRef<HTMLSpanElement | null>(null);
    const iconRef = useRef<HTMLDivElement | null>(null);
    const mergedRef = mergeRefs<HTMLSpanElement>(rippleRef, forwardedRef);

    useIsomorphicLayoutEffect(() => {
      if (enabled) {
        if (rippleRef.current) {
          animateRipple(rippleRef.current, { startScale, endScale });
        }
        if (iconRef.current) {
          scaleIcon(iconRef.current);
        }
      }
    }, [enabled, startScale, endScale]);

    return (
      <>
        <StyledRipple ref={mergedRef} $height={height} $width={width} />
        <IconContainer ref={iconRef} $isDisabled={isDisabled}>
          {children}
        </IconContainer>
      </>
    );
  }
);

const StyledRipple = styled.span<{
  $width: number;
  $height: number;
}>`
  background-color: ${theme.colors.background.interactive.activated};
  border-radius: 100%;
  height: ${(p) => `${p.$height}px`};
  left: 0;
  opacity: 1;
  position: absolute;
  top: 0;
  transform: scale(0);
  width: ${(p) => `${p.$width}px`};
`;

const IconContainer = styled.div<{ $isDisabled?: boolean }>`
  align-items: center;
  display: flex;
  height: ${theme.spaces[24]};
  justify-content: center;
  transform: scale(1);
  transition-duration: 50ms;
  transition-timing-function: ${theme.transitions.eases.primary};
  width: ${theme.spaces[24]};

  &:active {
    transform: ${(p) => (p.$isDisabled ? 'scale(1.0)' : 'scale(0.9)')};
  }
`;

type Scales = {
  startScale: number;
  endScale: number;
};

function animateRipple(el: HTMLElement, { startScale, endScale }: Scales) {
  el.animate(
    [
      { transform: `scale(${startScale})` },
      { transform: `scale(${endScale})` },
    ],
    {
      duration: 200,
      fill: 'both',
    }
  );

  el.animate([{ opacity: 1 }, { opacity: 0 }], {
    delay: 100,
    duration: 400,
    fill: 'both',
  });
}

function scaleIcon(el: HTMLElement) {
  el.animate(
    [
      { transform: 'scale(0.9)' },
      { transform: 'scale(1.15)' },
      { transform: 'scale(1)' },
    ],
    {
      duration: 200,
    }
  );
}
